import { AxiosResponse } from "axios";
import { useInfiniteQuery, useMutation } from "react-query";
import { Conversation, ConversationsStats, UpdateConversation } from "../../interfaces/conversations.type";
import { AppDispatch } from "../../store/store";
import { useDispatch } from "react-redux";
import { setConversations } from "../../store/slices/conversation.slice";
import { api } from "@data-phone/react-resource-server-auth";
import qs from "qs";
import { message } from "antd";

export interface ConversationQuery {
  did: string | null;
  limit?: number;
  offset?: number;
  unread?: boolean;
  assigned?: boolean;
  unassigned?: boolean;
  resolved?: boolean;
  assigned_user?: string | null;
  unresolved?: boolean;
  account?: string | null;
  participant?: string | null;
  participants?: string[];
  participants_match?: "exact" | "any";
  conversation_id?: string | null;
  searchQuery?: string | null;
}

export interface SearchConversationQuery {
  did: string
  search: string
  participants?: string[]
  searchType: "conversation" | "contact"
  before: string
}

export interface SearchedConversation {
  body: string
  did: string
  conversation_id: string
  participants: string[]
  type: "direct" | "group"
  id?: string
  send_id?: string
  position?: number
  unread_messages_count: number
  last_message_time: string
  timestamp?: string
}

async function fetchConversations(query: ConversationQuery & { stats: true }): Promise<ConversationsStats>
async function fetchConversations(query: ConversationQuery & { stats?: false }, offset: number): Promise<Conversation[]>
async function fetchConversations(query: ConversationQuery & { stats?: boolean }, offset?: number) {
  const queryStr = qs.stringify({
    limit: query.limit,
    offset: offset,
    participant: query.participant,
    participants: query.participants,
    participants_match: query.participants_match,
    conversation_id: query.conversation_id,
    assigned: query.assigned,
    assigned_user: query.assigned_user,
    unresolved: query.unresolved,
    unread: query.unread,
    resolved: query.resolved,
    unassigned: query.unassigned,
    account: query.account || undefined,
    search: query.searchQuery,
    stats: query.stats || false
  })

  const response: AxiosResponse = await api.get(`${process.env.REACT_APP_SMS_API_URL}/sms/conversations/${query.did}?${queryStr}`);

  return response.data;
}


export async function updateConversation(conversation: UpdateConversation): Promise<UpdateConversation | undefined> {
  try {
    const response: AxiosResponse<UpdateConversation> = await api.patch(
      `${process.env.REACT_APP_SMS_API_URL}/sms/conversations/update`,
      conversation
    );

    return response.data;
  } catch (error: any) {
    const errorText =
      error.response?.data?.message || error.message || "Unknown error";
    message.error(`Update failed: ${errorText}`);
  }

}

export const fetchConversationsData = async (
  query: ConversationQuery | null,
  offset: number,
  dispatch: AppDispatch
): Promise<Conversation[]> => {

  if (!query) return [];

  const data = await fetchConversations(query, offset);

  dispatch(setConversations({ data: data, query: query, offset: offset }));
  return data;
};

export const fetchConversationsStats = async (query: ConversationQuery): Promise<ConversationsStats> => {
  const data = await fetchConversations({ ...query, stats: true });

  return data;
};

export const fetchConversationByParticipants = async (
  did: string,
  participants: string[],
): Promise<Conversation | undefined> => {
  const conversations = await fetchConversations({
    did,
    participants,
    participants_match: "exact",
    limit: 1,
  }, 0);

  return conversations[0];
};


export const searchConversationsApi = async (searchQuery: SearchConversationQuery) => {
  const queryStr = qs.stringify({
    search: searchQuery.search,
    participants: searchQuery.participants,
    before: searchQuery.before,
    limit: 20
  }, { arrayFormat: "brackets" })

  const response: AxiosResponse<SearchedConversation[]> = await api.get(
    `${process.env.REACT_APP_SMS_API_URL}/sms/conversations/${searchQuery.did}/search/${searchQuery.searchType === "contact" ? "participants" : "messages"}?${queryStr}`,
  )
  return response.data
}

const useGetConversations = (query: ConversationQuery | null) => {

  const dispatch: AppDispatch = useDispatch();

  return useInfiniteQuery<Conversation[], Error>({
    queryKey: ["conversations", query],
    queryFn: ({ pageParam = 0 }) => fetchConversationsData(query, pageParam, dispatch),
    getNextPageParam(lastPage, allPages) {
      return lastPage.length > 0 ? allPages.length * 20 : undefined;
    },
    enabled: false
  });
};

export const useSearchConversations = () => {
  return useMutation<SearchedConversation[], Error, SearchConversationQuery>((query) => searchConversationsApi(query), { mutationKey: ["searchConversations"] })
}


export default useGetConversations;
